import type { FulfillmentSnapshot } from '../fulfillment/fulfillment-authority.ts';
import type { CanonicalOrderSnapshot } from '../order/order-finalization.ts';
import type { ValidatedPricingBenefit } from '../loyalty/benefit-authority.ts';
import type { ChannelEnvelope, ChannelSubmission } from '../channels/channel-intake.ts';
import {
  createChannelPaymentProofReview,
  type ApprovePaymentProofCommand,
  type ChannelPaymentProofSnapshot,
  type RequestPaymentProofResubmissionCommand,
  type ResubmitPaymentProofCommand,
} from '../channels/payment-proof-review.ts';
import { createC4CustomerLoyaltyControlPlane, type C4ReservedCoupon } from './c4-customer-loyalty.ts';
import {
  createBusinessRuntime as createBusinessRuntimeCore,
  type AcceptChannelOrderCommand,
  type ActiveOperatorWorkspaceOrderCommand,
  type BusinessRuntimeDependencies as CoreBusinessRuntimeDependencies,
  type OperatorOrderCommand,
  type OperatorWorkspaceOrderCommand,
  type RecordRuntimeCashTenderCommand,
  type RecordRuntimePaymentCommand,
  type RecordRuntimeRefundCommand,
  type RuntimeCashTenderResult,
  type RuntimeCatalogOptionGroupProjection,
  type RuntimeMutationCommand,
  type RuntimeOrderResult,
  type SmmChannelOrderCommand,
} from './business-runtime-core.ts';

export * from './business-runtime-core.ts';

/**
 * C2 is an optional C-owned domain hook on the single Business Runtime.
 * It never owns Product/Order/Fulfillment state; it receives their canonical
 * snapshots and projects ingredient effects through the existing C1 Inventory
 * authority owned behind the C2 port.
 */
export interface IngredientBomRuntimePort {
  readonly lockOrderPlan: (
    order: CanonicalOrderSnapshot,
    context: { readonly at: string },
  ) => unknown;
  readonly consumeProduction: (
    planId: string,
    fulfillment: FulfillmentSnapshot,
    context: { readonly actorId: string; readonly at: string },
  ) => unknown;
}

export interface BusinessRuntimeDependencies extends CoreBusinessRuntimeDependencies {
  readonly ingredientBom?: IngredientBomRuntimePort;
}

export interface RuntimeBenefitReservation {
  readonly couponId: string;
  readonly checkoutRef: string;
  readonly expectedCouponVersion: number;
  readonly validatedBenefit: ValidatedPricingBenefit;
}

export interface RuntimeCustomerProductProjection {
  readonly productId: string;
  readonly name: string;
  readonly currency: string;
  readonly basePriceMinor: number | null;
  readonly visible: boolean;
  readonly availability: 'available' | 'blocked' | 'unbound';
  readonly sellable: boolean;
  readonly requiresConfiguration: boolean;
  readonly hasOptions: boolean;
  readonly optionGroups: readonly RuntimeCatalogOptionGroupProjection[];
}

type BenefitAware<T> = T & {
  readonly benefitReservations?: readonly RuntimeBenefitReservation[];
};

const ingredientPlanId = (order: CanonicalOrderSnapshot): string =>
  `bom-plan:${order.orderId}:${order.revision}`;

const reservationsForCommit = (
  reservations: readonly RuntimeBenefitReservation[],
): { readonly checkoutRef: string | null; readonly records: readonly C4ReservedCoupon[]; readonly benefits: readonly ValidatedPricingBenefit[] } => {
  if (reservations.length === 0) return { checkoutRef: null, records: [], benefits: [] };
  const checkoutRef = reservations[0]!.checkoutRef.trim();
  if (!checkoutRef) throw new Error('C4_CHECKOUT_REF_REQUIRED');
  const seen = new Set<string>();
  const records: C4ReservedCoupon[] = [];
  const benefits: ValidatedPricingBenefit[] = [];
  for (const reservation of reservations) {
    if (reservation.checkoutRef.trim() !== checkoutRef) throw new Error('C4_CHECKOUT_REF_MISMATCH');
    if (seen.has(reservation.couponId)) throw new Error('C4_COUPON_RESERVATION_DUPLICATE');
    if (reservation.validatedBenefit.referenceId !== reservation.couponId) {
      throw new Error('C4_RESERVATION_BENEFIT_REFERENCE_MISMATCH');
    }
    if (!Number.isSafeInteger(reservation.expectedCouponVersion) || reservation.expectedCouponVersion < 1) {
      throw new Error('C4_COUPON_VERSION_INVALID');
    }
    seen.add(reservation.couponId);
    records.push({ couponId: reservation.couponId, version: reservation.expectedCouponVersion, checkoutRef });
    benefits.push(reservation.validatedBenefit);
  }
  return { checkoutRef, records, benefits };
};

/**
 * Public Business Runtime factory. C2 and C4 are composed into the same
 * canonical Runtime object; neither creates a second Order/Payment/
 * Fulfillment/Pricing truth.
 */
export function createBusinessRuntime(deps: BusinessRuntimeDependencies) {
  const core = createBusinessRuntimeCore(deps);
  const ingredientBom = deps.ingredientBom;
  const channelPaymentProofReview = createChannelPaymentProofReview();

  const customerProductProjection = (productId: string): RuntimeCustomerProductProjection | null => {
    const shared = core.productProjection(productId);
    const product = deps.getProduct(productId);
    if (!shared || !product) return null;
    const policy = deps.getActivePolicy();
    if (!policy) throw new Error('RUNTIME_ACTIVE_POLICY_REQUIRED');
    const visible = policy.visibility.customer[productId] === true;
    return {
      productId: shared.productId,
      name: product.names.customer,
      currency: shared.currency,
      basePriceMinor: shared.basePriceMinor,
      visible,
      availability: shared.availability,
      sellable: product.status === 'active' && visible && shared.availability === 'available' && shared.basePriceMinor !== null,
      requiresConfiguration: shared.requiresConfiguration,
      hasOptions: shared.hasOptions,
      optionGroups: shared.optionGroups,
    };
  };

  const c4 = createC4CustomerLoyaltyControlPlane({
    getOrder: (orderId) => core.getOrder(orderId),
    getPayment: (orderId) => core.paymentSnapshot(orderId),
    getPaymentEvents: (orderId) => deps.payment.events(orderId),
    getFulfillment: (orderId) => core.fulfillmentSnapshot(orderId),
    authorizeStaff: ({ actorId, permission, at }) => {
      if (!deps.staff) throw new Error(`STAFF_PERMISSION_DENIED:${permission}`);
      deps.staff.authorize({ actorId, permission, at });
    },
    appendFact: (fact) => deps.facts.append(fact),
  });

  const lockAcceptedOrder = <T extends RuntimeOrderResult>(result: T): T => {
    ingredientBom?.lockOrderPlan(result.order, { at: result.order.createdAt });
    return result;
  };

  const validateReservedCoupons = (
    reservations: readonly C4ReservedCoupon[],
    customerId: string | undefined,
  ): void => {
    for (const reservation of reservations) {
      const coupon = c4.benefit.coupon(reservation.couponId);
      if (!coupon || coupon.status !== 'reserved' || coupon.version !== reservation.version ||
          coupon.reservation?.checkoutRef !== reservation.checkoutRef) {
        throw new Error('C4_COUPON_RESERVATION_STALE');
      }
      if (customerId && c4.canonicalCustomerId(coupon.customerId) !== c4.canonicalCustomerId(customerId)) {
        throw new Error('BENEFIT_COUPON_CUSTOMER_MISMATCH');
      }
    }
  };

  const completePlacement = <T extends RuntimeOrderResult>(
    result: T,
    at: string,
    checkoutRef: string | null,
    reservations: readonly C4ReservedCoupon[],
  ): T => {
    lockAcceptedOrder(result);
    if (checkoutRef && reservations.length > 0) {
      c4.commitCoupons(result.order.orderId, checkoutRef, reservations, at);
    }
    return result;
  };

  const releaseAfterFailure = (
    checkoutRef: string | null,
    reservations: readonly C4ReservedCoupon[],
    at: string,
  ): void => {
    if (!checkoutRef || reservations.length === 0) return;
    c4.releaseCoupons(checkoutRef, reservations, at);
  };

  const appendChannelProofFact = (
    envelope: ChannelEnvelope,
    proof: ChannelPaymentProofSnapshot,
    factType: string,
    at: string,
    payload: unknown,
  ): void => {
    deps.facts.append({
      eventId: `channel:${envelope.submissionId}:payment-proof:${factType}:v${proof.version}`,
      aggregateType: 'channel_submission',
      aggregateId: envelope.submissionId,
      factType,
      businessDate: envelope.businessDate,
      occurredAt: at,
      recordedAt: at,
      schemaVersion: 1,
      source: {authority: 'channel-payment-proof', revision: String(proof.version)},
      payload,
    });
  };

  const submitChannel = (submission: ChannelSubmission): ChannelEnvelope => {
    const envelope = core.submitChannel(submission);
    if (envelope.payload.paymentMode === 'electronic-proof') {
      const evidenceRef = envelope.payload.paymentEvidenceRef;
      if (!evidenceRef) throw new Error('RUNTIME_PAYMENT_PROOF_EVIDENCE_REQUIRED');
      const proof = channelPaymentProofReview.submitInitial({
        submissionId: envelope.submissionId,
        evidenceRef,
        submittedAt: envelope.createdAt,
        submittedBy: envelope.source,
      });
      appendChannelProofFact(envelope, proof, 'payment_proof.submitted', envelope.createdAt, {
        evidenceRef: proof.currentEvidenceRef,
        source: envelope.source,
      });
    }
    return envelope;
  };

  const channelPaymentProof = (submissionId: string): ChannelPaymentProofSnapshot | null =>
    channelPaymentProofReview.get(submissionId);

  const requestChannelPaymentProofResubmission = (
    submissionId: string,
    command: RequestPaymentProofResubmissionCommand,
  ): ChannelPaymentProofSnapshot => {
    const envelope = core.previewChannelSubmission(submissionId).envelope;
    const proof = channelPaymentProofReview.requestResubmission(submissionId, command);
    appendChannelProofFact(envelope, proof, 'payment_proof.resubmission_requested', command.at, {
      reason: proof.reviewReason,
      actorId: command.actorId,
    });
    return proof;
  };

  const resubmitChannelPaymentProof = (
    submissionId: string,
    command: ResubmitPaymentProofCommand,
  ): ChannelPaymentProofSnapshot => {
    const envelope = core.previewChannelSubmission(submissionId).envelope;
    const proof = channelPaymentProofReview.resubmit(submissionId, command);
    appendChannelProofFact(envelope, proof, 'payment_proof.resubmitted', command.submittedAt, {
      evidenceRef: proof.currentEvidenceRef,
      submittedBy: command.submittedBy,
    });
    return proof;
  };

  const approveChannelPaymentProof = (
    submissionId: string,
    command: ApprovePaymentProofCommand,
  ): ChannelPaymentProofSnapshot => {
    const envelope = core.previewChannelSubmission(submissionId).envelope;
    const proof = channelPaymentProofReview.approve(submissionId, command);
    appendChannelProofFact(envelope, proof, 'payment_proof.approved', command.at, {
      evidenceRef: proof.currentEvidenceRef,
      actorId: command.actorId,
    });
    return proof;
  };

  const migrateApprovedChannelProof = (
    result: RuntimeOrderResult,
    envelope: ChannelEnvelope,
    proof: ChannelPaymentProofSnapshot,
    command: AcceptChannelOrderCommand,
  ): RuntimeOrderResult => {
    if (proof.status !== 'approved' || !proof.approvedAt || !proof.approvedBy) {
      throw new Error('RUNTIME_PAYMENT_PROOF_APPROVAL_REQUIRED');
    }
    const currentRecord = proof.records[proof.records.length - 1];
    if (!currentRecord) throw new Error('RUNTIME_PAYMENT_PROOF_RECORD_REQUIRED');
    const evidenceId = currentRecord.recordId;
    let evidence = deps.payment.evidence(result.order.orderId).find((entry) => entry.evidenceId === evidenceId);

    if (!evidence) {
      const beforeSubmit = deps.payment.snapshot(result.order.orderId);
      deps.payment.submitEvidence(result.order.orderId, {
        writer: 'smt-local',
        expectedVersion: beforeSubmit.version,
        idempotencyKey: `channel-proof:${envelope.submissionId}:submit:v${proof.version}`,
        operatorId: proof.approvedBy,
        at: proof.approvedAt,
        evidenceId,
        method: 'ELECTRONIC_PROOF',
        storageRef: proof.currentEvidenceRef,
        ...(envelope.payload.externalReference ? {reference: envelope.payload.externalReference} : {}),
      });
      evidence = deps.payment.evidence(result.order.orderId).find((entry) => entry.evidenceId === evidenceId);
    }

    if (!evidence) throw new Error('RUNTIME_PAYMENT_EVIDENCE_MIGRATION_FAILED');
    if (evidence.status === 'rejected') throw new Error('RUNTIME_PAYMENT_EVIDENCE_REJECTED');
    if (evidence.status === 'pending') {
      const beforeReview = deps.payment.snapshot(result.order.orderId);
      deps.payment.reviewEvidence(result.order.orderId, evidenceId, {
        writer: 'smt-local',
        expectedVersion: beforeReview.version,
        idempotencyKey: `channel-proof:${envelope.submissionId}:approve:v${proof.version}`,
        operatorId: proof.approvedBy,
        at: proof.approvedAt,
        decision: 'approved',
        amountMinor: result.order.totalMinor,
      });
    }

    const payment = deps.payment.snapshot(result.order.orderId);
    deps.facts.append({
      eventId: `payment:${result.order.orderId}:channel-proof:${envelope.submissionId}:v${proof.version}`,
      aggregateType: 'order',
      aggregateId: result.order.orderId,
      factType: 'payment.evidence_approved',
      businessDate: result.order.businessDate,
      occurredAt: proof.approvedAt,
      recordedAt: proof.approvedAt,
      schemaVersion: 1,
      source: {authority: 'payment', revision: String(payment.version)},
      payload: {
        submissionId: envelope.submissionId,
        evidenceId,
        method: 'ELECTRONIC_PROOF',
        amountMinor: result.order.totalMinor,
        operatorId: proof.approvedBy,
        fullySettled: payment.fullySettled,
      },
    });
    return {...result, payment};
  };

  const placeOperatorOrder = (command: BenefitAware<OperatorOrderCommand>): RuntimeOrderResult => {
    const { benefitReservations = [], ...coreCommand } = command;
    const prepared = reservationsForCommit(benefitReservations);
    validateReservedCoupons(prepared.records, coreCommand.customerId);
    try {
      return completePlacement(
        core.placeOperatorOrder(coreCommand, prepared.benefits),
        coreCommand.createdAt,
        prepared.checkoutRef,
        prepared.records,
      );
    } catch (error) {
      releaseAfterFailure(prepared.checkoutRef, prepared.records, coreCommand.createdAt);
      throw error;
    }
  };

  const placeOperatorWorkspaceOrder = (command: BenefitAware<OperatorWorkspaceOrderCommand>): RuntimeOrderResult => {
    const { benefitReservations = [], ...coreCommand } = command;
    const prepared = reservationsForCommit(benefitReservations);
    validateReservedCoupons(prepared.records, coreCommand.customerId);
    try {
      return completePlacement(
        core.placeOperatorWorkspaceOrder(coreCommand, prepared.benefits),
        coreCommand.createdAt,
        prepared.checkoutRef,
        prepared.records,
      );
    } catch (error) {
      releaseAfterFailure(prepared.checkoutRef, prepared.records, coreCommand.createdAt);
      throw error;
    }
  };

  const placeOperatorActiveWorkspaceOrder = async (
    command: BenefitAware<ActiveOperatorWorkspaceOrderCommand>,
  ): Promise<RuntimeOrderResult> => {
    const { benefitReservations = [], ...coreCommand } = command;
    const prepared = reservationsForCommit(benefitReservations);
    validateReservedCoupons(prepared.records, coreCommand.customerId);
    try {
      return completePlacement(
        await core.placeOperatorActiveWorkspaceOrder(coreCommand, prepared.benefits),
        coreCommand.createdAt,
        prepared.checkoutRef,
        prepared.records,
      );
    } catch (error) {
      releaseAfterFailure(prepared.checkoutRef, prepared.records, coreCommand.createdAt);
      throw error;
    }
  };

  const acceptChannelOrder = (command: BenefitAware<AcceptChannelOrderCommand>): RuntimeOrderResult => {
    const { benefitReservations = [], ...coreCommand } = command;
    const prepared = reservationsForCommit(benefitReservations);
    validateReservedCoupons(prepared.records, coreCommand.customerId);
    const envelope = core.previewChannelSubmission(coreCommand.submissionId).envelope;
    if (envelope.source === 'customer' && !envelope.payload.paymentMode) {
      throw new Error('RUNTIME_CUSTOMER_PAYMENT_MODE_REQUIRED');
    }
    const approvedProof = envelope.payload.paymentMode === 'electronic-proof'
      ? channelPaymentProofReview.get(envelope.submissionId)
      : null;
    if (envelope.payload.paymentMode === 'electronic-proof' && approvedProof?.status !== 'approved') {
      throw new Error('RUNTIME_PAYMENT_PROOF_APPROVAL_REQUIRED');
    }
    try {
      const accepted = core.acceptChannelOrder(coreCommand, prepared.benefits);
      const withPayment = approvedProof
        ? migrateApprovedChannelProof(accepted, envelope, approvedProof, coreCommand)
        : accepted;
      return completePlacement(
        withPayment,
        coreCommand.at,
        prepared.checkoutRef,
        prepared.records,
      );
    } catch (error) {
      releaseAfterFailure(prepared.checkoutRef, prepared.records, coreCommand.at);
      throw error;
    }
  };

  const acceptSmmChannelOrder = (command: BenefitAware<SmmChannelOrderCommand>): RuntimeOrderResult => {
    const { benefitReservations = [], ...coreCommand } = command;
    const prepared = reservationsForCommit(benefitReservations);
    validateReservedCoupons(prepared.records, coreCommand.customerId);
    try {
      return completePlacement(
        core.acceptSmmChannelOrder(coreCommand, prepared.benefits),
        coreCommand.at,
        prepared.checkoutRef,
        prepared.records,
      );
    } catch (error) {
      releaseAfterFailure(prepared.checkoutRef, prepared.records, coreCommand.at);
      throw error;
    }
  };

  const recordPayment = (orderId: string, command: RecordRuntimePaymentCommand) => {
    const result = core.recordPayment(orderId, command);
    c4.reconcileOrder(orderId, command.at);
    return result;
  };

  const recordCashTender = (orderId: string, command: RecordRuntimeCashTenderCommand): RuntimeCashTenderResult => {
    const result = core.recordCashTender(orderId, command);
    c4.reconcileOrder(orderId, command.at);
    return result;
  };

  const recordRefund = (orderId: string, command: RecordRuntimeRefundCommand) => {
    const result = core.recordRefund(orderId, command);
    c4.reconcileOrder(orderId, command.at);
    return result;
  };

  const markCompleted = (orderId: string, command: RuntimeMutationCommand): FulfillmentSnapshot => {
    const result = core.markCompleted(orderId, command);
    c4.reconcileOrder(orderId, command.at);
    return result;
  };

  const authorizeProduction = (
    orderId: string,
    command: RuntimeMutationCommand,
  ): FulfillmentSnapshot => {
    const result = core.authorizeProduction(orderId, command);
    if (ingredientBom) {
      const order = core.getOrder(orderId);
      if (!order) throw new Error('RUNTIME_ORDER_NOT_FOUND');
      ingredientBom.consumeProduction(
        ingredientPlanId(order),
        result,
        { actorId: command.operatorId, at: command.at },
      );
    }
    return result;
  };

  return {
    ...core,
    c4,
    customerProductProjection,
    submitChannel,
    channelPaymentProof,
    requestChannelPaymentProofResubmission,
    resubmitChannelPaymentProof,
    approveChannelPaymentProof,
    paymentEvidence: (orderId: string) => deps.payment.evidence(orderId),
    paymentEvents: (orderId: string) => deps.payment.events(orderId),
    placeOperatorOrder,
    placeOperatorWorkspaceOrder,
    placeOperatorActiveWorkspaceOrder,
    acceptChannelOrder,
    acceptSmmChannelOrder,
    recordPayment,
    recordCashTender,
    recordRefund,
    markCompleted,
    authorizeProduction,
  };
}
